import React from 'react';
import { useGame } from '../context/GameContext';
import { CARDS } from '../game/cards';
import { Swords, Skull, AlertTriangle } from 'lucide-react';

// v2.2 ELITE SKIP-FOR-CURSE — modal-like screen shown right before elite combat.
// Player chooses to FIGHT the elite (normal flow) or SKIP and add a random curse card to their deck.

export default function EliteChoice() {
  const { pendingEliteChoice, eliteFight, eliteSkip } = useGame();
  if (!pendingEliteChoice) return null;
  const { enemy, cmod, skipCurse, floorIdx } = pendingEliteChoice;
  const curseCard = CARDS[skipCurse];

  return (
    <div className="min-h-screen w-full px-6 py-8 flex flex-col items-center justify-center gap-8" data-testid="elite-choice-screen">
      <div className="text-center">
        <div className="text-xs uppercase tracking-[0.4em] text-[color:var(--text-dim)]" data-testid="elite-choice-header-subtitle">
          FLOOR {(floorIdx || 0) + 1} · ELITE ENCOUNTER
        </div>
        <h1 className="font-display text-4xl sm:text-6xl font-black uppercase text-[color:var(--red)] mt-2">
          FIGHT OR FLEE
        </h1>
        <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)] mt-2 max-w-lg mx-auto">
          Choose your fate. There is no reward for cowardice — only survival.
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-5xl w-full">
        {/* FIGHT card */}
        <button
          onClick={eliteFight}
          className="brutal-border p-6 bg-[#0a0a0a] flex flex-col items-center gap-4 text-center hover:translate-x-[-3px] hover:translate-y-[-3px] transition-transform"
          data-testid="elite-fight-btn"
          style={{ borderColor: 'var(--red)', boxShadow: '6px 6px 0 var(--red)' }}
        >
          <div className="text-6xl">{enemy.sprite || '💀'}</div>
          <div className="font-display font-black text-2xl uppercase text-[color:var(--red)]">
            {enemy.name}
          </div>
          <div className="text-xs uppercase font-mono text-[color:var(--text-dim)]">
            HP {enemy.maxHp}
          </div>
          {cmod && (
            <div className="text-[10px] uppercase font-mono text-[color:var(--pink)] flex items-center gap-1" data-testid="elite-modifier">
              <AlertTriangle size={12}/> MODIFIER: {cmod.name}
            </div>
          )}
          <div className="mt-2 flex items-center gap-2 text-xs uppercase tracking-widest text-[color:var(--neon)] font-display font-black">
            <Swords size={16}/> FIGHT · CARD REWARD + GOLD
          </div>
        </button>

        {/* SKIP card */}
        <button
          onClick={eliteSkip}
          className="brutal-border p-6 bg-[#0a0a0a] flex flex-col items-center gap-4 text-center hover:translate-x-[-3px] hover:translate-y-[-3px] transition-transform"
          data-testid="elite-skip-btn"
          style={{ borderColor: '#666', boxShadow: '6px 6px 0 #666' }}
        >
          <div className="text-6xl">{curseCard?.art || '💀'}</div>
          <div className="font-display font-black text-2xl uppercase text-[color:var(--text-dim)]">
            {curseCard?.name || 'CURSE'}
          </div>
          <div className="text-[11px] font-mono text-[color:var(--text-dim)] max-w-[220px]">
            {curseCard?.desc || 'A permanent curse card added to your deck.'}
          </div>
          <div className="text-[10px] uppercase font-mono text-[color:var(--red)] mt-1 flex items-center gap-1">
            <Skull size={12}/> ADDED TO YOUR DECK PERMANENTLY
          </div>
          <div className="mt-2 flex items-center gap-2 text-xs uppercase tracking-widest text-[color:var(--pink)] font-display font-black">
            SKIP · NO REWARD
          </div>
        </button>
      </div>
    </div>
  );
}
